import React, { useEffect, useState, useRef, useCallback } from "react";
import "./tournament.css";
import { database } from "../../database/firebase";
import { ref, get, set, onValue } from "firebase/database";

function TournamentPage({ updateHeader }) {
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const [matchType, setMatchType] = useState(null);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [popup, setPopup] = useState(null);
  const joinRedirectTimeoutRef = useRef(null);

  useEffect(() => {
    return () => {
      if (joinRedirectTimeoutRef.current) {
        clearTimeout(joinRedirectTimeoutRef.current);
        joinRedirectTimeoutRef.current = null;
      }
    };
  }, []);

  const closePopup = () => setPopup(null);

  // 🔹 Fetch tournament list

  useEffect(() => {
    const stored = sessionStorage.getItem("userData");
    if (stored) setUserData(JSON.parse(stored));
  }, []);

  // Back button goes to Rules
  useEffect(() => {
    updateHeader({ backButtonRedirect: "/rules" });
  }, [updateHeader]);

  // 🔹 Ensure session match_type exists (default: 'live') and read it
  useEffect(() => {
    if (!userData?.sessionId) return;

    // Realtime DB path: sessions/{sessionId}/match_type
    const sessionMatchTypeRef = ref(
      database,
      `sessions/${userData.sessionId}/match_type`
    );

    const ensureMatchType = async () => {
      try {
        const snap = await get(sessionMatchTypeRef);
        if (!snap.exists()) {
          // No match_type yet → default to 'live'
          await set(sessionMatchTypeRef, "live");
          setMatchType("live");
          return;
        }

        const value = snap.val();
        if (!value) {
          await set(sessionMatchTypeRef, "live");
          setMatchType("live");
        } else {
          setMatchType(value);
        }
      } catch (err) {
        console.error("Error checking session match_type", err);
      }
    };

    ensureMatchType();
  }, [userData]);

  // 🔹 If session is in 'live' mode, redirect to Ludo live game
  useEffect(() => {
    console.log(userData);
    console.log(matchType);
    if (!userData || !matchType) return;
    if (matchType !== "live") return;

    // Show loader while redirecting
    setIsRedirecting(true);

    const params = new URLSearchParams({
      mode: "live",
      name: userData.name || "",
      userId: userData.userId || "",
    });

    // Small delay to allow React to render the loader before redirect
    setTimeout(() => {
      window.location.href = `/ludo-game?${params.toString()}`;
    }, 100);
  }, [matchType, userData]);

  const fetchTournaments = useCallback(async () => {
    try {
      const res = await fetch(
        process.env.REACT_APP_FOLDER_LINK+"/api/getTournamentList.php",
        {
          method: "POST",
          headers: {
            "Content-Type": "text/plain",
          },
          body: JSON.stringify({
            sessionId: userData.sessionId,
            userId: userData.userId,
          }),
        }
      );

      const data = await res.json();
      setTournaments(data.tournaments || []);
    } catch (err) {
      console.error(err);
      console.log("failed to load tournaments");
      // Swal.fire("Error", "Failed to load tournaments", "error");
    } finally {
      setLoading(false);
    }
  }, [userData]);

  useEffect(() => {
    if (!userData) return;
    fetchTournaments();
  }, [userData, fetchTournaments]);

  // 🔹 Firebase trigger: when admin generates a tournament, React should refetch list.
  const lastTournamentGenKeyRef = useRef(null);
  const hasInitializedGenKeyRef = useRef(false);
  useEffect(() => {
    if (!userData?.sessionId) return;

    // Ignore the first snapshot value (it fires immediately on subscribe).
    // We only want the popup when the key changes after page load.
    hasInitializedGenKeyRef.current = false;

    const genKeyRef = ref(
      database,
      `sessions/${userData.sessionId}/tournament_generated_key`
    );

    const unsubscribe = onValue(genKeyRef, (snap) => {
      const key = snap.val();
      if (!key) return;

      if (!hasInitializedGenKeyRef.current) {
        hasInitializedGenKeyRef.current = true;
        lastTournamentGenKeyRef.current = key;
        return;
      }

      if (key === lastTournamentGenKeyRef.current) return;
      lastTournamentGenKeyRef.current = key;

      fetchTournaments();
      setPopup({
        variant: "info",
        title: "Tournament Generated",
        message: "Tournament has been generated.",
      });
    });

    return () => unsubscribe();
  }, [userData, fetchTournaments]);

  // 🔹 Join tournament
  const joinTournament = async (tournament) => {
    try {
      const res = await fetch(
        process.env.REACT_APP_FOLDER_LINK+"/api/joinTournament.php",
        {
          method: "POST",
          headers: {
            "Content-Type": "text/plain",
          },
          body: JSON.stringify({
            sessionId: userData.sessionId,
            tournamentId: tournament.tournamentId,
            name: userData.name,
            email: userData.email,
            userId: userData.userId,
          }),
        }
      );

      const data = await res.json();

      const success =
        (data && data.response && data.response.success) ?? data?.success;
      const tournamentIdFromApi =
        (data && data.response && data.response.tournamentId) ??
        data?.tournamentId ??
        tournament.tournamentId;

      if (success) {
        setPopup({
          variant: "success",
          title: "Registered",
          message:
            "You've successfully registered for the tournament",
        });
        fetchTournaments();
        if (joinRedirectTimeoutRef.current) {
          clearTimeout(joinRedirectTimeoutRef.current);
          joinRedirectTimeoutRef.current = null;
        }
        if (tournamentIdFromApi) {
          joinRedirectTimeoutRef.current = setTimeout(() => {
            joinRedirectTimeoutRef.current = null;
            window.location.href =
              "/tournament-structure/" + tournamentIdFromApi;
          }, 5000);
        }
      } else {
        setPopup({
          variant: "info",
          title: "",
          message: data?.response?.error || "Failed",
        });
      }
    } catch (err) {
      console.error(err);
      setPopup({
        variant: "error",
        title: "Error",
        message: "Something went wrong",
      });
    }
  };

  // 🔹 Button logic (same as PHP) + deadline/generated from API
  const renderButton = (t) => {
    // Deadline passed: show "Deadline Passed" unless bracket is generated, then show "Open"
    if (t.deadline) {
      if (t.generated) {
        return (
          <button
            className="view-button"
            onClick={() =>
              (window.location.href =
                "/tournament-structure/" + t.tournamentId)
            }
          >
            Open
          </button>
        );
      }
      return (
        <button className="deadline-passed-button" disabled>
          Deadline Passed
        </button>
      );
    }

    if (t.generated) {
      if (t.alreadyJoined) {
        return (
          <button
            className="view-button"
            onClick={() =>
              (window.location.href =
                "/tournament-structure/" + t.tournamentId)
            }
          >
            Open
          </button>
        );
      }
      return (
        <button
          className="view-button"
          onClick={() =>
            (window.location.href =
              "/tournament-structure/" + t.tournamentId)
          }
        >
          View
        </button>
      );
    }

    if (!t.alreadyJoined) {
      return (
        <button
          className="join-button"
          onClick={() => joinTournament(t)}
        >
          Register
        </button>
      );
    }

    return <button className="joined">Registered</button>;
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleString("en-IN", {
      day: "2-digit",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const popupModal = popup && (
    <div
      className="tournament-simple-popup-backdrop"
      onClick={closePopup}
      role="presentation"
    >
      <div
        className={
          "tournament-simple-popup-card tournament-simple-popup-card--" +
          popup.variant
        }
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={popup.title ? "tournament-simple-popup-title" : undefined}
        aria-describedby="tournament-simple-popup-desc"
      >
        <div className="tournament-simple-popup-dice-wrap" aria-hidden="true">
          <span className="tournament-simple-popup-dice">🎲</span>
        </div>
        {popup.title ? (
          <h2
            id="tournament-simple-popup-title"
            className="tournament-simple-popup-title"
          >
            {popup.title}
          </h2>
        ) : null}
        <p
          id="tournament-simple-popup-desc"
          className={
            "tournament-simple-popup-message" +
            (!popup.title ? " tournament-simple-popup-message--solo" : "")
          }
        >
          {popup.message}
        </p>
        <button
          type="button"
          className="tournament-simple-popup-ok"
          onClick={closePopup}
        >
          OK
        </button>
      </div>
    </div>
  );

  // Show loader until matchType is determined from Firebase
  if (!matchType) {
    return (
      <div className="tournament-wrapper">
        {popupModal}
        <div className="tournament-table">
          <div style={{ 
            color: "#fff", 
            textAlign: "center", 
            padding: "50px 20px",
            fontSize: "18px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "20px"
          }}>
            <div className="spinner" style={{
              border: "4px solid rgba(255, 255, 255, 0.3)",
              borderTop: "4px solid #fff",
              borderRadius: "50%",
              width: "50px",
              height: "50px",
              animation: "spin 1s linear infinite"
            }}></div>
            <div>Loading...</div>
          </div>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // Show redirect loader when redirecting
  if (isRedirecting || matchType === "live") {
    return (
      <div className="tournament-wrapper">
        {popupModal}
        <div className="tournament-table">
          <div style={{ 
            color: "#fff", 
            textAlign: "center", 
            padding: "50px 20px",
            fontSize: "18px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "20px"
          }}>
            <div className="spinner" style={{
              border: "4px solid rgba(255, 255, 255, 0.3)",
              borderTop: "4px solid #fff",
              borderRadius: "50%",
              width: "50px",
              height: "50px",
              animation: "spin 1s linear infinite"
            }}></div>
            <div>Redirecting to game...</div>
          </div>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div className="tournament-wrapper">
      {popupModal}
      <div className="tournament-title">Tournaments</div>

      <div className="tournament-table">
        {loading ? (
          <div style={{ color: "#fff", textAlign: "center" }}>
            Loading...
          </div>
        ) : (
          <table className="table table-striped">
            <thead>
              <tr>
                <th>Tournament Name</th>
                <th>Start Date</th>
                <th>Joining Deadline</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {tournaments.map((t, i) => (
                <tr key={i}>
                  <td data-label="Tournament Name">{t.name}</td>
                  <td data-label="Start Date">{formatDate(t.startDate)}</td>
                  <td data-label="Joining Deadline">{formatDate(t.deadlineDate)}</td>
                  <td data-label="Action">{renderButton(t)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Decorative images */}
        <img src="/images/key1.png" className="top-left-image" alt="" />
        <img src="/images/key2.png" className="top-right-image" alt="" />
      </div>
    </div>
  );
}

export default TournamentPage;
